import { Container, Stack, Typography } from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";

import useLocales from "@hooks/useLocales";
import usePermissions from "@hooks/usePermissions";
import { PATH_DASHBOARD, PATH_PROCUREMENT } from "@routes/paths";
import HeaderBreadcrumbs from "src/components/HeaderBreadcrumbs";
import Page from "src/components/Page";
import { TableFilterWrapper } from "src/components/filter-table-wrapper";
import { usePurchaseRequisitionApi } from "../../hooks/usePurchaseRequisitionApi";
import { getPurchaseRequisitions } from "../../services/purchaseRequisitionService";
import {
  IPurchaseRequisition,
  IPurchaseRequisitionListParams,
} from "../../types/purchaseRequisitionTypes";
import ProcurementFilters, {
  IProcurementFilterState,
} from "./ProcurementFilters";
import ProcurementTable from "./ProcurementTable";

const DEFAULT_ROWS_PER_PAGE = 10;

const PurchaseRequisitionDrafts = () => {
  const { translate } = useLocales();
  const { permissionHandle } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState<IPurchaseRequisition[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [sortBy, setSortBy] = useState("createdDate");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [filterState, setFilterState] = useState<IProcurementFilterState>({
    documentNumber: "",
    requesterName: "",
    vendorId: "",
    dueDeliveryDate: null as Date | null,
    isSaveAsDraft: true,
  });

  const fetchList = async (
    currentPage: number,
    currentRowsPerPage: number,
    filters?: IProcurementFilterState,
  ) => {
    setLoading(true);
    try {
      const params: IPurchaseRequisitionListParams = {
        page: currentPage,
        record: currentRowsPerPage,
        documentNumber: filters?.documentNumber ?? "",
        requesterName: filters?.requesterName,
        vendorId: filters?.vendorId,
        dueDeliveryDate: filters?.dueDeliveryDate,
        isSaveAsDraft: true,
      };

      const res = await getPurchaseRequisitions(params);
      if (!res.error && res.data) {
        setRows(res.data.purchaseRequisitions || []);
        setHasNextPage(res.data.hasNextPage);
      } else if (res.forbidden) {
        enqueueSnackbar(translate("permission_denied"), { variant: "error" });
      } else {
        setRows([]);
        setHasNextPage(false);
      }
    } catch (error) {
      enqueueSnackbar(translate("something_went_wrong"), { variant: "error" });
      setRows([]);
      setHasNextPage(false);
    }
    setLoading(false);
  };

  const handlePageChange = (_: any, newPage: number) => {
    setPage(newPage + 1);
    fetchList(newPage + 1, rowsPerPage, filterState);
  };

  const handleRowsPerPageChange = (
    event: SelectChangeEvent<HTMLInputElement>,
  ) => {
    const newRowsPerPage = parseInt(event.target.value as string, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(1);
    fetchList(1, newRowsPerPage, filterState);
  };

  const handleSortChange = (field: string, order: "asc" | "desc") => {
    setSortBy(field);
    setSortOrder(order);
    setPage(1);
    fetchList(1, rowsPerPage, filterState);
  };

  useEffect(() => {
    setPage(1);
    fetchList(1, rowsPerPage, filterState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterState]);

  useEffect(() => {
    fetchList(page, rowsPerPage, filterState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  // Using handleDelete and handleFinalize from the shared hook
  const { handleDelete, handlePost } = usePurchaseRequisitionApi(() =>
    fetchList(page, rowsPerPage, filterState),
  );

  return (
    <Page title={translate("procurement")}>
      <Container maxWidth={false}>
        <HeaderBreadcrumbs
          links={[
            { name: translate("Dashboard"), href: PATH_DASHBOARD.root },
            {
              name: translate("procurement"),
            },
            {
              name: translate("purchase_requisitions"),
              href: PATH_PROCUREMENT.general.purchaseRequisition.general
                .purchaseRequisitionList,
            },
            {
              name: translate("purchase_requisition_drafts"),
            },
          ]}
        />

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 3, mt: 1 }}
        >
          <Typography variant="h3" fontWeight="bold" textTransform="capitalize">
            {translate("purchase_requisition_drafts")}
          </Typography>
        </Stack>

        <TableFilterWrapper>
          <TableFilterWrapper.Header>
            <ProcurementFilters
              loader={loading}
              filterState={filterState}
              setFilterState={setFilterState}
              isDraft
            />
          </TableFilterWrapper.Header>
          <TableFilterWrapper.Content>
            <ProcurementTable
              rows={rows}
              loading={loading}
              page={page}
              rowsPerPage={rowsPerPage}
              hasNextPage={hasNextPage}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={handleSortChange}
              onDelete={handleDelete}
              onPost={handlePost}
              isDraft
            />
          </TableFilterWrapper.Content>
        </TableFilterWrapper>
      </Container>
    </Page>
  );
};

export default PurchaseRequisitionDrafts;
